/**
 * FleetMessageTemplates — Paramètres flotte : 5 templates messages perso.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { RotateCcw, Save, Mail, Info } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  fine_notify: "🚨 Notification d'amende au conducteur",
  designation_ask: "📝 Demande désignation conducteur",
  technical_control_reminder: "🔧 Rappel contrôle technique",
  insurance_expiry_reminder: "🛡️ Rappel expiration assurance",
  claim_notify: "💥 Notification sinistre au conducteur",
};

const VARS = {
  fine_notify: ["driver_name", "vehicle_plate", "infraction_date", "location", "reason", "amount", "payment_deadline", "company_name"],
  designation_ask: ["vehicle_plate", "infraction_date", "location", "designation_deadline", "company_name"],
  technical_control_reminder: ["driver_name", "vehicle_plate", "ct_date", "days_left", "company_name"],
  insurance_expiry_reminder: ["vehicle_plate", "insurance_end_date", "days_left", "company_name"],
  claim_notify: ["driver_name", "vehicle_plate", "claim_date", "location", "claim_type", "description", "company_name"],
};

export default function FleetMessageTemplates() {
  const [templates, setTemplates] = useState([]);
  const [notifSettings, setNotifSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [tr, sr] = await Promise.all([
        api.get("/fleet/message-templates"),
        api.get("/fleet/notifications/settings"),
      ]);
      setTemplates(tr.data.templates || []);
      setNotifSettings(sr.data || {});
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (t) => {
    try {
      await api.put(`/fleet/message-templates/${t.type}`, {
        type: t.type, subject: t.subject, body: t.body, enabled: t.enabled ?? true,
      });
      toast.success("Template enregistré");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const reset = async (type) => {
    if (!window.confirm("Restaurer le template par défaut ?")) return;
    try {
      await api.post(`/fleet/message-templates/${type}/reset`);
      toast.success("Restauré");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const upd = (idx, k, v) => setTemplates(prev => prev.map((t, i) => i === idx ? { ...t, [k]: v } : t));

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const parseDays = (s) => (s || "").toString().split(",").map(x => parseInt(x.trim())).filter(x => !isNaN(x) && x > 0);
      await api.put("/fleet/notifications/settings", {
        notification_email: notifSettings.notification_email,
        ct_reminder_days: Array.isArray(notifSettings.ct_reminder_days) ? notifSettings.ct_reminder_days : parseDays(notifSettings.ct_reminder_days),
        insurance_reminder_days: Array.isArray(notifSettings.insurance_reminder_days) ? notifSettings.insurance_reminder_days : parseDays(notifSettings.insurance_reminder_days),
        lease_reminder_days: Array.isArray(notifSettings.lease_reminder_days) ? notifSettings.lease_reminder_days : parseDays(notifSettings.lease_reminder_days),
        fine_notify_enabled: !!notifSettings.fine_notify_enabled,
        claim_notify_enabled: !!notifSettings.claim_notify_enabled,
        reminders_enabled: !!notifSettings.reminders_enabled,
      });
      toast.success("Paramètres enregistrés");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingSettings(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="fleet-templates-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Paramètres notifications Parc Auto</h1>
        <p className="text-sm text-slate-500">Cadences & templates messages personnalisables — jamais en dur.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4" />Cadence des rappels (CRON quotidien)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Rappels activés</p>
              <p className="text-xs text-slate-500">CRON quotidien envoie CT + assurance aux conducteurs assignés</p>
            </div>
            <Switch checked={!!notifSettings.reminders_enabled} onCheckedChange={v => setNotifSettings({ ...notifSettings, reminders_enabled: v })} data-testid="reminders-switch" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Jalons rappel contrôle technique (jours)</Label>
              <Input type="text" placeholder="90,60,30,7"
                          value={Array.isArray(notifSettings.ct_reminder_days) ? notifSettings.ct_reminder_days.join(",") : (notifSettings.ct_reminder_days || "")}
                          onChange={e => setNotifSettings({ ...notifSettings, ct_reminder_days: e.target.value })}
                          data-testid="ct-days-input" />
              <p className="text-[10px] text-slate-400 mt-1">Séparés par virgule. Défaut : 90, 60, 30, 7 jours avant échéance</p>
            </div>
            <div>
              <Label>Jalons rappel assurance (jours)</Label>
              <Input type="text" placeholder="90,60,30,7"
                          value={Array.isArray(notifSettings.insurance_reminder_days) ? notifSettings.insurance_reminder_days.join(",") : (notifSettings.insurance_reminder_days || "")}
                          onChange={e => setNotifSettings({ ...notifSettings, insurance_reminder_days: e.target.value })}
                          data-testid="ins-days-input" />
              <p className="text-[10px] text-slate-400 mt-1">Séparés par virgule</p>
            </div>
            <div>
              <Label>Jalons fin de contrat leasing / LLD / LOA (jours)</Label>
              <Input type="text" placeholder="60"
                          value={Array.isArray(notifSettings.lease_reminder_days) ? notifSettings.lease_reminder_days.join(",") : (notifSettings.lease_reminder_days || "")}
                          onChange={e => setNotifSettings({ ...notifSettings, lease_reminder_days: e.target.value })}
                          data-testid="lease-days-input" />
              <p className="text-[10px] text-slate-400 mt-1">Envoyé à l'email gestionnaire (ou au dirigeant). Défaut : 60 jours avant la fin</p>
            </div>
            <div>
              <Label>Email fallback (si conducteur non assigné)</Label>
              <Input type="email" placeholder="admin@entreprise.fr"
                          value={notifSettings.notification_email || ""}
                          onChange={e => setNotifSettings({ ...notifSettings, notification_email: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div><p className="text-sm font-medium">Notif amende</p><p className="text-[10px] text-slate-500">Envoi auto au conducteur</p></div>
              <Switch checked={!!notifSettings.fine_notify_enabled} onCheckedChange={v => setNotifSettings({ ...notifSettings, fine_notify_enabled: v })} />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div><p className="text-sm font-medium">Notif sinistre</p><p className="text-[10px] text-slate-500">Envoi auto au conducteur</p></div>
              <Switch checked={!!notifSettings.claim_notify_enabled} onCheckedChange={v => setNotifSettings({ ...notifSettings, claim_notify_enabled: v })} />
            </div>
          </div>
          <Button onClick={saveSettings} disabled={savingSettings} data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-2" />Enregistrer paramètres
          </Button>
        </CardContent>
      </Card>

      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800 flex gap-2">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <span>Chaque template est <b>entièrement modifiable</b>. Variables disponibles : <code className="text-[10px]">{`{{ variable }}`}</code>. Bouton « Restaurer défaut » pour revenir au template d'origine.</span>
      </div>

      {templates.map((t, i) => (
        <Card key={t.type} data-testid={`tpl-${t.type}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{TYPE_LABEL[t.type] || t.type}</CardTitle>
              <div className="flex items-center gap-2">
                {t.is_default && <Badge variant="outline" className="text-[10px]">Défaut</Badge>}
                <Button size="sm" variant="ghost" onClick={() => reset(t.type)} data-testid={`reset-${t.type}`}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />Défaut
                </Button>
                <Button size="sm" onClick={() => save(t)} data-testid={`save-${t.type}`}>
                  <Save className="w-3.5 h-3.5 mr-1" />Enregistrer
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Variables : {(VARS[t.type] || []).map(v => `{{ ${v} }}`).join(" · ")}</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Objet email</Label>
              <Input value={t.subject || ""} onChange={e => upd(i, "subject", e.target.value)} data-testid={`subject-${t.type}`} />
            </div>
            <div>
              <Label>Corps du message</Label>
              <Textarea rows={7} value={t.body || ""} onChange={e => upd(i, "body", e.target.value)}
                              className="font-mono text-xs" data-testid={`body-${t.type}`} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
